import React, { useCallback, useMemo, useState } from 'react';
import {
  IconCancel,
  Spinner_v2 as Spinner,
  priceNormalize,
  SupportedCurrency,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import { IonSpinner } from '@ionic/react';
import {
  StyledFailureOrSuccessMessage,
  StyledPaymentPromoCodeForm,
  StyledPromoCodeButton,
  StyledPromoCodeInput,
} from '../../components/PaymentPromoCode/PaymentPromoCode.styled';
import { useIntl } from 'react-intl';
import { CouponPreviewDetailsType } from '../../shared';
import { useTranslation } from 'react-i18next';
import useToast from '../../context/ToastContext/ToastContext';
import { IPromotionalCode } from 'src/pages/types';

type DiscountInputFeatureProps = {
  isExpanded: boolean | null;
  setIsExpanded: (value: boolean | null) => void;
  isLoading: boolean;
  onSubmit: (value: string) => void;
  onCancel?: () => Promise<void>;
  failureMessage: string;
  couponPreviewDetails?: CouponPreviewDetailsType | null;
  promoCodeData?: IPromotionalCode | null;
  showLoader?: boolean;

  type: 'promo' | 'coupon';
};

const DiscountInputFeature: React.FC<DiscountInputFeatureProps> = ({
  isExpanded,
  setIsExpanded,
  onSubmit,
  isLoading,
  failureMessage,
  couponPreviewDetails,
  promoCodeData,
  onCancel,
  showLoader,
  type,
}) => {
  const intl = useIntl();
  const { t } = useTranslation(['booking', 'gift-certificates', 'trial-session']);
  const { showToast } = useToast();
  const [value, setValue] = useState('');

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      onSubmit(value);
    },
    [value]
  );

  const handleCancel = useCallback(async () => {
    try {
      await onCancel?.();
      setValue('');
    } catch (error) {
      showToast({ error });
    }
  }, [onCancel]);

  const chipText = useMemo(() => {
    if (type === 'promo') {
      return t('booking:promocode.add_button');
    }
    return t('gift-certificates:checkout.gift_card_button');
  }, [type]);

  if (showLoader) {
    return (
      <div className="flex w-full items-center justify-center !h-[70px]">
        <Spinner size={32} />
      </div>
    );
  }

  if (couponPreviewDetails || promoCodeData) {
    return (
      <div className="flex gap-1 w-full flex-col">
        <div className="flex justify-between items-center">
          <div
            className={`flex items-center gap-2 py-1 pl-4 ${type === 'coupon' && couponPreviewDetails?.coupon_type !== 'balance' ? 'pr-2' : 'pr-4'} bg-color-black text-color-white rounded-full`}
          >
            <Typography variant={TypographyVariantsEnum.Text14Semi} className={'text-color-white'}>
              {couponPreviewDetails?.coupon_type === 'balance'
                ? t('trial-session:coupon_balance.title')
                : couponPreviewDetails?.coupon_code ?? promoCodeData?.code}
            </Typography>
            {onCancel && couponPreviewDetails?.coupon_type !== 'balance' && (
              <IconCancel className={'cursor-pointer w-4 h-4'} color={'#8B8C8D'} onClick={handleCancel} />
            )}
          </div>
          <Typography variant={TypographyVariantsEnum.Text18Regular}>
            -{' '}
            {priceNormalize({
              price: promoCodeData?.discount_amount ?? Number(couponPreviewDetails?.coupon_discount_amount) / 100,
              currency:
                (promoCodeData?.transaction_currency as unknown as SupportedCurrency) ??
                (couponPreviewDetails?.transaction_currency as unknown as SupportedCurrency),
              extendedUsd: true,
            })}
          </Typography>
        </div>
        {type === 'coupon' && (
          <div className={'flex items-end flex-col'}>
            <Typography variant={TypographyVariantsEnum.Text14Regular} opacity={50}>
              {t(
                couponPreviewDetails?.coupon_type === 'balance'
                  ? 'trial-session:balance_caption'
                  : 'gift-certificates:checkout.gift_card.balance',
                {
                  amount: priceNormalize({
                    price: Number(couponPreviewDetails?.coupon_initial_balance) / 100,
                    currency: couponPreviewDetails?.transaction_currency as unknown as SupportedCurrency,
                    extendedUsd: true,
                  }),
                }
              )}
            </Typography>
            <Typography variant={TypographyVariantsEnum.Text14Regular} opacity={50}>
              {t(
                couponPreviewDetails?.coupon_type === 'balance'
                  ? 'trial-session:remains_caption'
                  : 'gift-certificates:checkout.gift_card.balance_after_purchase',
                {
                  amount: priceNormalize({
                    price: Number(couponPreviewDetails?.coupon_remaining_balance) / 100,
                    currency: couponPreviewDetails?.transaction_currency as unknown as SupportedCurrency,
                    extendedUsd: true,
                  }),
                }
              )}
            </Typography>
          </div>
        )}
      </div>
    );
  }

  if (!isExpanded) {
    return (
      <button onClick={() => setIsExpanded(true)} className="flex items-center gap-2 px-4 rounded-full bg-[#F5F7FA]">
        <span className="text-lg">+</span>
        <Typography variant={TypographyVariantsEnum.Text14Semi}>{chipText}</Typography>
      </button>
    );
  }

  return (
    <>
      <StyledPaymentPromoCodeForm onSubmit={handleSubmit} error={failureMessage?.length > 1}>
        <StyledPromoCodeInput
          className={'queryStatus'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          name={'promoCodeInput'}
          placeholder={
            type === 'coupon'
              ? t('gift-certificates:checkout.gift_card_input.hint')
              : intl.formatMessage({
                  id: 'promo.placeholder',
                  defaultMessage: 'Промокод',
                })
          }
        />
        <StyledPromoCodeButton disabled={!value} className={!value || isLoading ? 'disabled' : ''} type={'submit'}>
          {isLoading ? (
            <IonSpinner name="lines" color="light" />
          ) : (
            <span>{t('gift-certificates:checkout.gift_card_input.add_button')}</span>
          )}
        </StyledPromoCodeButton>
      </StyledPaymentPromoCodeForm>
      {!!failureMessage.length && (
        <StyledFailureOrSuccessMessage className={'failure'}>
          <span>{type === 'promo' ? failureMessage : t('gift-certificates:checkout.gift_card.invalid_error')}</span>
        </StyledFailureOrSuccessMessage>
      )}
    </>
  );
};

export default DiscountInputFeature;